import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * Matches parsing logic in StateMandateCompliance.jsx
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseStateMandateComplianceFromApiResponse(
  response,
  targetUserId,
) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.compliance) {
    return result.compliance;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.clinicianName != null ||
      result.applicantName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * @param {Record<string, unknown>} c
 */
function getClinicianDisplayRaw(c) {
  if (c.clinicianName) return String(c.clinicianName).trim();
  if (c.applicantName) return String(c.applicantName).trim();
  const u = c.user;
  if (u && typeof u === "object") {
    const fn = /** @type {{ firstName?: string }} */ (u).firstName || "";
    const ln = /** @type {{ lastName?: string }} */ (u).lastName || "";
    const t = `${fn} ${ln}`.trim();
    if (t) return t;
  }
  return "";
}

/**
 * @param {Record<string, unknown>} compliance
 * @param {{ companyName: string; logoUrl: string; seniorStaffNurse?: string }} branding
 */
export function buildStateMandateCompliancePrintHtml(compliance, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");
  const donRaw = String(branding.seniorStaffNurse || "").trim();
  const donName = donRaw ? escapeHtml(donRaw) : "—";

  const clinicianHtml = escapeHtml(getClinicianDisplayRaw(compliance));

  const es = compliance.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    compliance.applicantSignature ||
    compliance.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      compliance.applicantSignedAt ||
        compliance.applicantSignatureDate ||
        compliance.empSignedAt,
    ),
  );

  const reviewedBySignedBy = escapeHtml(
    String(compliance.reviewedBySignedBy || ""),
  );
  const reviewedBySig =
    compliance.reviewedBySignature || compliance.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      compliance.reviewedBySignedAt ||
        compliance.adminSignatureDate ||
        compliance.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!compliance.applicantStatementAccepted;
  const statusCompleted =
    String(compliance.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>State Mandate Compliance — ${clinicianHtml || "Clinician"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 13px; font-weight: 500; color: #2563eb; margin: 0 0 2px 0; }
    .title-co { font-size: 11px; color: #6b7280; margin: 0; }
    h2.section-title {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 8px;
      margin: 20px 0 12px 0;
    }
    .auth-box {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 16px;
      margin-top: 8px;
    }
    .auth-box p {
      margin: 0 0 12px 0;
      text-align: justify;
      font-size: 11px;
      color: #374151;
    }
    .auth-box p:last-child { margin-bottom: 0; }
    .auth-box .uline { text-decoration: underline; font-weight: 600; }
    .don-box {
      margin: 16px 0;
      padding: 16px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 10px;
    }
    .don-box p { margin: 0 0 8px 0; font-size: 11px; color: #374151; }
    .don-name { font-size: 14px; font-weight: 700; color: #111827; margin: 8px 0 4px 0; }
    .don-title { font-size: 12px; font-weight: 600; color: #2563eb; margin: 0; }
    .ack-head {
      font-weight: 700;
      font-size: 12px;
      color: #111827;
      margin: 20px 0 12px 0;
      padding-top: 12px;
      border-top: 2px solid #e5e7eb;
    }
    .field-label {
      font-size: 10px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 4px;
    }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 38px;
      background: #fff;
      font-size: 12px;
      color: #111827;
      margin-bottom: 16px;
    }
    .cert-box {
      margin-top: 16px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 11px; letter-spacing: 0.02em; margin: 0 0 4px 0; }
    .cert-sub { font-size: 9px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 11px;
    }
    .chk { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 {
      font-size: 12px;
      font-weight: 700;
      margin: 0 0 10px 0;
      color: #111827;
    }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .sig-img { max-height: 48px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">State Mandate Compliance</p>
        <p class="title-sub">Patient records</p>
        <p class="title-co">${company}</p>
      </div>
    </div>

    <h2 class="section-title">Notice</h2>
    <div class="auth-box">
      <p>
        Title 22 of California Code of Regulations, Division 5, Chapter
        6, is the California Law that governs all HOME HEALTH AGENCIES.
        Section 74735, Patient Health Records Paragraph (5) states
        "CLINICAL NOTES SHALL BE SIGNED AND INCORPORATED INTO THE
        PATIENT'S HEALTH RECORD AT LEAST EVERY SEVEN (7) WORKING DAYS".
      </p>
      <p>
        All <strong>${company}</strong> clinicians must comply with this
        State Mandate. The Agency will require all clinicians to submit
        the patient visit notes within five (5) days of the visit date,
        and no later than 5:00 P.M. on Monday.
        <span class="uline">There will be no exceptions.</span>
      </p>
      <p>
        <strong>Note:</strong> Notes that are incomplete, missing
        signature(s), or need clarification will be returned to the
        clinician for corrections. Therefore, clinicians must submit
        their notes in a timely manner to allow for review, corrections,
        final completion and filing into patient's charts, so that both
        the clinicians and the Agency remain compliant with the STATE
        REGULATIONS.
      </p>
    </div>

    <div class="don-box">
      <p><em>Thanking you in advance for your cooperation,</em></p>
      <p class="don-name">${donName}</p>
      <p class="don-title">Director of Nursing</p>
    </div>

    <p class="ack-head">
      Acknowledgement of receipt of "PATIENT RECORDS - STATE MANDATE
      COMPLIANCE" Notice and AGREEMENT TO COMPLY with the above
      mentioned State and Agency Regulations.
    </p>
    <div class="field-label">Clinician name</div>
    <div class="field-value">${clinicianHtml || "—"}</div>

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee / clinician</h3>
        <div class="sig-label">Name</div>
        <div class="field-value" style="margin-bottom:10px;">${clinicianHtml || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value" style="margin-bottom:10px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
